import { invoke, API_CONFIG } from "../utils/apiClient";

export const getMerchantPermissions = async (token = null) => {
  try {
    const response = await invoke("GET", `/keys/get-key-permissions`, null, {
      token,
    });

    return response?.data?.data;
  } catch (error) {
    console.error("❌ Error getMerchantPermissions:", error.message);
    throw error;
  }
};

export const getMerchantSettings = async (token = null) => {
  try {
    const response = await invoke("GET", `/merchant/settings`, null, {
      token,
    });

    return response?.data?.data;
  } catch (error) {
    console.error("❌ Error getMerchantSettings:", error.message);
    throw error;
  }
};

/**
 * Create KYC Inquiry
 */
export const createInquiry = async (inquiryId, token = null) => {
  try {
    const response = await invoke(
      "POST",
      `/inquiry/create/${inquiryId}`,
      null,
      { token }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Error creating inquiry:", error.message);
    throw error;
  }
};

/**
 * Submit KYC Inquiry with Complete Data
 */
export const submitInquiry = async (inquiryId, inquiryData, token = null) => {
  if (!inquiryData) {
    throw new Error("submitInquiry: inquiryData is undefined");
  }

  const formData = new FormData();
  formData.append("submissionId", inquiryId);
  formData.append("fullName", inquiryData.name || "John Doe");
  formData.append("country", inquiryData.country || "Pakistan");
  formData.append("dateOfBirth", inquiryData.dateOfBirth || "1990-01-01");

  if (inquiryData.idFront)
    formData.append("idFront", inquiryData.idFront, "id_front.jpg");
  if (inquiryData.idBack)
    formData.append("idBack", inquiryData.idBack, "id_back.jpg");
  if (inquiryData.drivingLicenceFront)
    formData.append(
      "drivingLicenceFront",
      inquiryData.drivingLicenceFront,
      "license_front.jpg"
    );
  if (inquiryData.drivingLicenceBack)
    formData.append(
      "drivingLicenceBack",
      inquiryData.drivingLicenceBack,
      "license_back.jpg"
    );
  if (inquiryData.video)
    formData.append("video", inquiryData.video, "liveness.mp4");

  const response = await invoke(
    "POST",
    `/inquiry/submit/${inquiryId}`,
    formData,
    {
      token,
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return response.data;
};

/**
 * Complete KYC Inquiry
 */
export const completeInquiry = async (inquiryId, token = null) => {
  try {
    const url = `/inquiry/${inquiryId}/complete`;

    const response = await invoke("POST", url, null, { token });

    // Return both status and data for proper error handling
    return {
      status: response.status,
      statusCode: response.data?.statusCode || response.status,
      data: response.data,
    };
  } catch (error) {
    console.error("❌ Error completing inquiry:", error.message);
    throw error;
  }
};

/**
 * Submit Single Document to KYC Inquiry
 * Used for submitting documents one at a time (front, back, video)
 */
export const submitSingleDocument = async (
  inquiryId,
  documentData,
  token = null
) => {
  if (!documentData) {
    throw new Error("submitSingleDocument: documentData is undefined");
  }

  const formData = new FormData();

  // Include personal info only if provided (typically on first submission)
  if (documentData.fullName) {
    formData.append("fullName", documentData.fullName);
  }
  if (documentData.country) {
    formData.append("country", documentData.country);
  }
  if (documentData.dateOfBirth) {
    formData.append("dateOfBirth", documentData.dateOfBirth);
  }
  if (documentData.documentType) {
    formData.append("documentType", documentData.documentType);
  }

  // Append the document based on type
  if (documentData.idFront) {
    formData.append("idFront", documentData.idFront, "id_front.jpg");
  }
  if (documentData.idBack) {
    formData.append("idBack", documentData.idBack, "id_back.jpg");
  }
  if (documentData.passport) {
    formData.append("passport", documentData.passport, "passport.jpg");
  }
  if (documentData.photo) {
    formData.append("photo", documentData.photo, "photo.jpg");
  }
  if (documentData.drivingLicenceFront) {
    formData.append(
      "drivingLicenceFront",
      documentData.drivingLicenceFront,
      "license_front.jpg"
    );
  }
  if (documentData.drivingLicenceBack) {
    formData.append(
      "drivingLicenceBack",
      documentData.drivingLicenceBack,
      "license_back.jpg"
    );
  }
  if (documentData.video) {
    let videoBlob = documentData.video;
    const type = videoBlob.type || "video/mp4";
    const ext = type.includes("webm") ? "webm" : "mp4";

    const videoFile = new File([videoBlob], `liveness_${Date.now()}.${ext}`, {
      type,
      lastModified: Date.now(),
    });

    formData.append("video", videoFile, videoFile.name);
  }
  if (documentData.utilityBill) {
    const filename = documentData.utilityBill.name || "utility_bill.jpg";
    formData.append("utilityBill", documentData.utilityBill, filename);
  }
  if (documentData.bankStatement) {
    const filename = documentData.bankStatement.name || "bank_statement.jpg";
    formData.append("bankStatement", documentData.bankStatement, filename);
  }
  if (documentData.governmentIssuedDocument) {
    const filename = documentData.governmentIssuedDocument.name || "govt_document.jpg";
    formData.append("governmentIssuedDocument", documentData.governmentIssuedDocument, filename);
  }
  if (documentData.nfcData) {
    formData.append(
      "nfcData",
      typeof documentData.nfcData === "string"
        ? documentData.nfcData
        : JSON.stringify(documentData.nfcData)
    );
  }
  if (documentData.nfcChipImage) {
    const filename = documentData.nfcChipImage.name || "nfc_chip.jpg";
    formData.append("nfcChipImage", documentData.nfcChipImage, filename);
  }

  const response = await invoke(
    "POST",
    `/inquiry/submit-single/${inquiryId}`,
    formData,
    {
      token,
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  // Return both status and data for proper error handling
  return {
    status: response.status,
    statusCode: response.data?.statusCode || response.status,
    data: response.data,
  };
};

/**
 * Get Inquiry Details
 * Retrieves complete inquiry data including user info and submitted documents
 */
export const getInquiryDetails = async (inquiryId, token = null) => {
  try {
    const response = await invoke("GET", `/inquiry/${inquiryId}`, null, {
      token,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error getting inquiry details:", error.message);
    throw error;
  }
};

/**
 * Get Inquiry Status
 */
export const getInquiryStatus = async (inquiryId, token = null) => {
  try {
    const response = await invoke("GET", `/inquiry/status/${inquiryId}`, null, {
      token,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error getting inquiry status:", error.message);
    throw error;
  }
};

/**
 * Update User Meta
 * Sends user details to VerifiLite in step one
 */
export const updateUserMeta = async (inquiryId, personalInfo, token = null) => {
  try {
    const apiKey = process.env.REACT_APP_VERIFILITE_API_KEY || "YOUR_API_KEY";
    const apiSecret =
      process.env.REACT_APP_VERIFILITE_API_SECRET || "YOUR_API_SECRET";

    const response = await invoke(
      "POST",
      `/inquiry/user-meta/${inquiryId}`,
      {
        fullName: personalInfo.fullName,
        dateOfBirth: personalInfo.dateOfBirth,
        country: personalInfo.country,
      },
      {
        token,
        // headers: {
        //   "api-key": apiKey,
        //   "api-secret": apiSecret,
        // },
      }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Error updating user meta:", error.message);
    throw error;
  }
};
